import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { CurrencyMaskConfig } from 'ng2-currency-mask';
import { Subscription } from 'rxjs';

import { CaixaService } from 'src/app/core/services/http/caixa.service';
import { KeyboardKeysEnum } from '../../enums/keyboard-keys.enum';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { CaixaConferenciaItens, CaixaConferenciaRequest } from '../../models/caixa-conferencia-request';
import { HelpersUtil } from '../../utils/helpers.util';
import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { UsuarioModel } from '../../models/usuario.model';
import { ImpressaoTermicaService } from 'src/app/core/services/behavior/impressao-termica.service';

@Component({
  selector: 'app-fechamento-caixa-dialog',
  templateUrl: './fechamento-caixa-dialog.component.html',
  styleUrls: ['./fechamento-caixa-dialog.component.scss']
})
export class FechamentoCaixaDialogComponent implements OnInit, OnDestroy {
  private readonly _logoutAposFechamentoStorageKey = 'logoutAposFechamentoObrigatorio';
  public readonly optionsMask: CurrencyMaskConfig = {
    prefix: '',
    thousands: '.',
    decimal: ',',
    align: 'right',
    allowNegative: false,
    precision: 2,
    suffix: ''
  };

  private _subs: Subscription[] = [];

  public infoPgtos: CaixaConferencia[] = [];
  public fechamentoConcluido = false;
  public isFechandoCaixa = false;
  public mensagemFechamentoFormatada = '';
  public exibirResumoFechamento = false;
  numeroCaixa: number;
  codigoCabCaixa: number = 0;
  private _logoutDisparado = false;

  podeVerQuantiaCaixa = false;

  constructor(
    private _actionSheetCtrl: ActionSheetController,
    private _modalCtrl: ModalController,
    private _caixaService: CaixaService,
    private _authService: AuthenticationService,
    private _storageService: StorageService,
    private _impressaoTermicaService: ImpressaoTermicaService
  ) 
  {
    this.numeroCaixa = this._authService.numeroCaixa;

    this._caixaService.verificarCaixaAberto().subscribe(dataR =>
      {
          this.codigoCabCaixa = dataR.data.codigo;
      });
   }

  public async cancelarAsyncOnClick(): Promise<void> {
    if (this.fechamentoConcluido) {
      await this._encerrarSessaoAposFechamentoAsync();
      return;
    }

    await this._modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
  }

  public async confirmarFechamentoCaixaAsyncOnClick(): Promise<void> {
    await this._confirmarFechamentoCaixaAsync();
  }

  public exibirAsyncOnClick(): void {
    this.exibirResumoFechamento = true;
    this.imprimirResumoFechamento();
  }

  public mudarFocoInputsConferenciaOnKeydownEnter(indexInputAtual: number): void {
    const inputs = this._obterInputsConferencia();
    const indexProximoInput = (indexInputAtual + 1) % inputs.length;
    const proximoInput = inputs[indexProximoInput];

    proximoInput.focus();
  }

  public ngOnDestroy(): void {
    this._subs.forEach(l => l.unsubscribe());

    if (this.fechamentoConcluido && !this._logoutDisparado) {
      this._logoutDisparado = true;

      void this._storageService.remove(this._logoutAposFechamentoStorageKey);

      void this._authService.logout({
        removeDatabaseName: false,
        removeBaseUrl: false
      });
    }
  }

  public async ngOnInit(): Promise<void> {
    
    const sub$ = this._caixaService.obterDadosFechamentoCaixa(this.numeroCaixa).subscribe(res => {
      const pgtosResponse = res.success ? res.data : [];

      pgtosResponse.forEach(l => this.infoPgtos.push(new CaixaConferencia(l.conferencia, l.formaPgto, l.total, l.codigo)))
    });

    this._subs.push(sub$);

    const usuarioLogado = await this._storageService.get('user') as UsuarioModel;

    this.podeVerQuantiaCaixa = usuarioLogado.verQuantiaCaixa === true;
  }

  @HostListener('window:keydown', ['$event'])
  public async _lidarComKeyboardEventAsync(event: KeyboardEvent): Promise<void> {
    if (event.key === KeyboardKeysEnum.F5) {
      event.preventDefault();
      event.stopPropagation();

      this._confirmarFechamentoCaixaAsync();
    }
  }

  private async _confirmarFechamentoCaixaAsync(): Promise<void> {
    if (this.isFechandoCaixa || this.fechamentoConcluido) {
      return;
    }

    const request = this._prepararCaixaConferenciaRequest();

    const total = HelpersUtil.sumArray(request.itens, 'total');
    const totalConferencia = HelpersUtil.sumArray(request.itens, 'conferencia');

    if (total != totalConferencia) {
      const confirmou = await this._validarFechamentoCaixaComDivergenciaAsync();

      if (!confirmou) return;
    }

    this.isFechandoCaixa = true;

    const sub$ = this._caixaService.fecharCaixa(request)
      .subscribe(async res => {
        this.isFechandoCaixa = false;

        if (res.success) {
          this.fechamentoConcluido = true;
          this.mensagemFechamentoFormatada = this._formatarMensagemFechamento(res.data);
          this.exibirResumoFechamento = true;
          await this._storageService.set(this._logoutAposFechamentoStorageKey, true);
        }
      });

    this._subs.push(sub$);
  }

  private _definirFocoPrimeiroInputConferencia(): void {
    const inputs = this._obterInputsConferencia();

    const primeiroInput = inputs[0];

    primeiroInput.focus();
  }

  private _obterInputsConferencia(): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll('input.inputConferencia') as NodeListOf<HTMLInputElement>;
  }

  private _prepararCaixaConferenciaRequest(): CaixaConferenciaRequest {
    const request = new CaixaConferenciaRequest();
    request.codigoCaixaCab = this.codigoCabCaixa;

    this.infoPgtos.forEach(caixaConferencia => request.itens.push(new CaixaConferenciaItens(caixaConferencia.codigo, caixaConferencia.total, caixaConferencia.conferencia)));
    return request;
  }

  private async _validarFechamentoCaixaComDivergenciaAsync(): Promise<boolean> {
    const actionSheet = await this._actionSheetCtrl.create({
      header: 'Existe divergência no valor do fechamento.',
      subHeader: 'Fechar assim mesmo?',
      buttons: [
        {
          text: 'Sim',
          role: ModalRoleEnum.CONFIRM,
          id: 'btnSimActionSheet'
        },
        {
          text: 'Não',
          role: ModalRoleEnum.CANCEL,
          id: 'btnNaoActionSheet'
        },
      ],
      backdropDismiss: false,
      cssClass: 'custom-action-sheet'
    });

    await actionSheet.present();

    const btnSim = document.querySelector('#btnSimActionSheet') as HTMLButtonElement;

    HelpersUtil.executeAfterDelay(() => btnSim.focus(), 1);

    const { role } = await actionSheet.onWillDismiss();

    return role === ModalRoleEnum.CONFIRM;
  }

  private _formatarMensagemFechamento(mensagem: string | null | undefined): string {
    return this._impressaoTermicaService.normalizarTexto(
      mensagem,
      ''
    );
  }

  private imprimirResumoFechamento(): void {
    this._impressaoTermicaService.imprimirTexto(
      this.mensagemFechamentoFormatada,
      ''
    );
  }

  private async _encerrarSessaoAposFechamentoAsync(): Promise<void> {
    if (this._logoutDisparado) {
      return;
    }

    this._logoutDisparado = true;

    await this._storageService.remove(this._logoutAposFechamentoStorageKey);
    await this._modalCtrl.dismiss(null, ModalRoleEnum.CONFIRM);
    await this._authService.logout({
      removeDatabaseName: false,
      removeBaseUrl: false
    });
  }
}

export class CaixaConferencia {
  private _conferencia: number;
  private _total: number;

  public avista: boolean;
  public codigo: number;
  public formaPgto: string;
  public image: any;

  constructor(
    conferencia: number,
    formaPgto: string,
    total: number,
    codigoFormaPgto: number
  ) {
    this.formaPgto = formaPgto;

    this._conferencia = conferencia;
    this._total = total;

    this.codigo = codigoFormaPgto;
  }

  public get conferencia(): number {
    return this._conferencia;
  }

  public set conferencia(v: number) {
    this._conferencia = v;
  }

  public get diferenca(): number {
    return this._conferencia - this._total;
  }

  public get total(): number {
    return this._total;
  }
}
